import { atom } from "jotai";

/**
 * Interface for tracking authentication state during field mapping
 */
export interface AuthenticationState {
  /** The implementation ID this auth is associated with */
  implementation_id?: string;
  /** The selected authentication ID to use */
  auth_id?: string;
  /** The authentication label/name for display */
  auth_label?: string;
  /** Current authentication status */
  status?: "valid" | "expired" | "testing" | "unknown" | "missing";
  /** Whether authentication has been confirmed by user */
  confirmed?: boolean;
  /** Timestamp when auth was last verified */
  last_checked?: string;
  /** Whether user needs to select from multiple auths */
  needs_selection?: boolean;
  /** Available authentications for selection */
  available_auths?: Array<{
    id: string;
    label: string;
    status: string;
  }>;
  /** ENFORCEMENT: Whether explicit user confirmation is required */
  requires_user_confirmation?: boolean;
  /** ENFORCEMENT: Timestamp of user confirmation */
  user_confirmation_timestamp?: number;
  /** ENFORCEMENT: Whether AI should block field mapping */
  blocks_field_mapping?: boolean;
}

/**
 * Atom to track the current authentication state during field mapping
 * This helps maintain auth context across multiple tool calls
 */
export const selectedAuthAtom = atom<AuthenticationState>({});
selectedAuthAtom.debugLabel = "selectedAuthAtom";

/**
 * Derived atom to check if authentication is ready for field mapping
 * ENHANCED: Now includes enforcement checks for user confirmation
 */
export const authReadyAtom = atom((get) => {
  const auth = get(selectedAuthAtom);
  const now = Date.now();

  // Basic auth requirements
  const hasBasicAuth = !!(
    auth.implementation_id &&
    auth.auth_id &&
    auth.confirmed &&
    (auth.status === "valid" || auth.status === "testing")
  );

  if (!hasBasicAuth) {
    return false;
  }

  // ENFORCEMENT: Check if user confirmation is explicitly required
  if (auth.requires_user_confirmation) {
    if (!auth.user_confirmation_timestamp) {
      return false;
    }

    // User confirmation expires after 30 minutes for security
    const CONFIRMATION_EXPIRY = 30 * 60 * 1000;
    if (now - auth.user_confirmation_timestamp > CONFIRMATION_EXPIRY) {
      return false;
    }
  }

  return true;
});
authReadyAtom.debugLabel = "authReadyAtom";

/**
 * Write-only atom to update authentication state
 */
export const updateAuthStateAtom = atom(
  null,
  (get, set, update: Partial<AuthenticationState>) => {
    const current = get(selectedAuthAtom);
    set(selectedAuthAtom, {
      ...current,
      ...update,
      last_checked: new Date().toISOString(),
    });
  },
);
updateAuthStateAtom.debugLabel = "updateAuthStateAtom";

/**
 * Write-only atom to clear authentication state
 */
export const clearAuthStateAtom = atom(null, (get, set) => {
  set(selectedAuthAtom, {});
});
clearAuthStateAtom.debugLabel = "clearAuthStateAtom";

/**
 * ENFORCEMENT: Atom to set user confirmation explicitly
 */
export const setUserAuthConfirmationAtom = atom(
  null,
  (
    get,
    set,
    confirmation: {
      implementation_id: string;
      auth_id: string;
      auth_label: string;
      confirmed: boolean;
    },
  ) => {
    const current = get(selectedAuthAtom);
    set(selectedAuthAtom, {
      ...current,
      implementation_id: confirmation.implementation_id,
      auth_id: confirmation.auth_id,
      auth_label: confirmation.auth_label,
      confirmed: confirmation.confirmed,
      user_confirmation_timestamp: confirmation.confirmed
        ? Date.now()
        : undefined,
      requires_user_confirmation: false, // Clear the requirement once confirmed
      blocks_field_mapping: !confirmation.confirmed, // Stop blocking if confirmed
      last_checked: new Date().toISOString(),
    });
  },
);
setUserAuthConfirmationAtom.debugLabel = "setUserAuthConfirmationAtom";

/**
 * ENFORCEMENT: Atom to check if field mapping is currently blocked
 */
export const isFieldMappingBlockedAtom = atom((get) => {
  const auth = get(selectedAuthAtom);

  // Block if explicitly flagged
  if (auth.blocks_field_mapping) {
    return true;
  }

  // Block if user confirmation is required but not provided
  if (auth.requires_user_confirmation && !auth.user_confirmation_timestamp) {
    return true;
  }

  // Block if no basic auth information
  if (!auth.implementation_id || !auth.auth_id || !auth.confirmed) {
    return true;
  }

  return false;
});
isFieldMappingBlockedAtom.debugLabel = "isFieldMappingBlockedAtom";
